import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PublicContentPageShell from '@components/layout/PublicContentPageShell';
import { brandColors } from '@config/brandTokens';
import {
  MOTOR_CAPABILITIES,
  MOTOR_DESIGN_DECISIONS,
  MOTOR_FIT_LEGEND,
  MOTOR_FRAMEWORK_LAYERS,
  MOTOR_FRAMEWORK_PURPOSE,
  MOTOR_FRAMEWORK_REFERENCES,
  MOTOR_SPORT_OPTIONS,
} from '@/content/motorPsychomotorData';

type PageTab = 'summary' | 'superset';

const fitColor = (fit: string) => {
  const f = String(fit || '').toLowerCase();
  if (f === 'direct') return { bg: 'rgba(76,177,35,0.14)', color: brandColors.green };
  if (f === 'transfer') return { bg: 'rgba(74,111,235,0.12)', color: brandColors.blue };
  if (f === 'limited') return { bg: 'rgba(245,158,11,0.16)', color: '#b45309' };
  return { bg: '#f1f5f9', color: brandColors.slate };
};

const sectionTitleSx = {
  fontWeight: 800,
  color: brandColors.navy,
  mb: 1,
  fontSize: '1rem',
  fontFamily: '"Sora", Arial, sans-serif',
} as const;

const MotorPsychomotorPage: React.FC = () => {
  const [tab, setTab] = useState<PageTab>('superset');
  const [selectedSports, setSelectedSports] = useState<string[]>(
    MOTOR_SPORT_OPTIONS.map((s) => s.id),
  );
  const [layerFilter, setLayerFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const categories = useMemo(() => {
    const set = new Set<string>();
    MOTOR_CAPABILITIES.forEach((s) => {
      const c = String(s.category || '').trim();
      if (c) set.add(c);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, []);

  const layers = useMemo(() => {
    const set = new Set<string>();
    MOTOR_CAPABILITIES.forEach((s) => {
      const layer = String(s.frameworkLayer || '').trim();
      if (layer) set.add(layer);
    });
    return [...set];
  }, []);

  const filteredSkills = useMemo(() => {
    return MOTOR_CAPABILITIES.filter((s) => {
      const layer = String(s.frameworkLayer || '').trim();
      const category = String(s.category || '').trim();
      if (layerFilter !== 'all' && layer !== layerFilter) return false;
      if (categoryFilter !== 'all' && category !== categoryFilter) return false;
      return true;
    });
  }, [layerFilter, categoryFilter]);

  const activeSports = useMemo(
    () => MOTOR_SPORT_OPTIONS.filter((s) => selectedSports.includes(s.id)),
    [selectedSports],
  );

  const toggleSport = (_: React.MouseEvent<HTMLElement>, next: string[]) => {
    if (next.length) setSelectedSports(next);
  };

  return (
    <PublicContentPageShell
      maxWidth={false}
      title="Motor & Psychomotor skills"
      subtitle="Universal movement / psychomotor superset with sport-by-sport fit. Choose sports to compare; filter the matrix by framework layer or category."
    >
      <Box
        role="tablist"
        aria-label="Motor and psychomotor sections"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 0.5, md: 1 },
          mb: 2.5,
          borderBottom: '2px solid #e2e8f0',
        }}
      >
        {(
          [
            { id: 'summary' as const, label: 'Framework & summary' },
            { id: 'superset' as const, label: 'Movement / psychomotor superset' },
          ] as const
        ).map((item) => {
          const active = tab === item.id;
          return (
            <Box
              key={item.id}
              component="button"
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(item.id)}
              sx={{
                appearance: 'none',
                border: 'none',
                borderBottom: active ? `3px solid ${brandColors.navy}` : '3px solid transparent',
                bgcolor: 'transparent',
                color: active ? brandColors.navy : brandColors.slate,
                fontFamily: '"Sora", Arial, sans-serif',
                fontWeight: active ? 800 : 600,
                fontSize: { xs: '0.92rem', md: '1rem' },
                px: { xs: 1.25, md: 1.75 },
                pt: 0.75,
                pb: 1.15,
                mb: '-2px',
                cursor: 'pointer',
                lineHeight: 1.3,
                borderRadius: 0,
                transition: 'color 120ms ease, border-color 120ms ease',
                '&:hover': {
                  color: brandColors.navy,
                  bgcolor: 'transparent',
                },
              }}
            >
              {item.label}
            </Box>
          );
        })}
      </Box>

      {tab === 'summary' ? (
        <Box>
          <Typography sx={{ color: brandColors.slate, mb: 2, lineHeight: 1.55, maxWidth: 960 }}>
            {MOTOR_FRAMEWORK_PURPOSE}
          </Typography>

          <Typography sx={sectionTitleSx}>Framework layers</Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: '1px solid #e2e8f0', mb: 2.5, borderRadius: 2 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Layer</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>What it means</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Use across sports</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }} align="right">
                    Count
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOTOR_FRAMEWORK_LAYERS.map((layer) => (
                  <TableRow key={layer.id} hover>
                    <TableCell sx={{ fontWeight: 700, color: brandColors.navy, whiteSpace: 'nowrap' }}>
                      {layer.name}
                    </TableCell>
                    <TableCell sx={{ color: brandColors.slate }}>{layer.meaning}</TableCell>
                    <TableCell sx={{ color: brandColors.slate }}>{layer.useInPlatform}</TableCell>
                    <TableCell align="right">{layer.count ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography sx={sectionTitleSx}>Sport fit legend</Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2.5 }}>
            {MOTOR_FIT_LEGEND.map((item) => {
              const c = fitColor(item.code);
              return (
                <Chip
                  key={item.code}
                  label={`${item.code}: ${item.meaning}`}
                  sx={{
                    bgcolor: c.bg,
                    color: c.color,
                    fontWeight: 600,
                    height: 'auto',
                    py: 0.75,
                    maxWidth: '100%',
                    '& .MuiChip-label': { whiteSpace: 'normal' },
                  }}
                />
              );
            })}
          </Stack>

          <Typography sx={sectionTitleSx}>Key design decisions</Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: '1px solid #e2e8f0', mb: 2.5, borderRadius: 2 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Decision</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Why</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Practical consequence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOTOR_DESIGN_DECISIONS.map((row) => (
                  <TableRow key={row.decision} hover>
                    <TableCell sx={{ fontWeight: 700, color: brandColors.navy }}>{row.decision}</TableCell>
                    <TableCell sx={{ color: brandColors.slate }}>{row.why}</TableCell>
                    <TableCell sx={{ color: brandColors.slate }}>{row.consequence}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography sx={sectionTitleSx}>Official framework references</Typography>
          <Stack spacing={1}>
            {MOTOR_FRAMEWORK_REFERENCES.map((ref) => (
              <Box
                key={ref.title}
                sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: brandColors.white }}
              >
                <Typography sx={{ fontWeight: 700, color: brandColors.navy }}>{ref.title}</Typography>
                <Typography sx={{ color: brandColors.slate, fontSize: '0.9rem', mt: 0.35 }}>
                  {ref.summary}
                </Typography>
                {ref.url ? (
                  <Link
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: '0.85rem', fontWeight: 600 }}
                  >
                    Open reference
                  </Link>
                ) : null}
              </Box>
            ))}
          </Stack>
        </Box>
      ) : (
        <Box>
          <Typography sx={{ color: brandColors.slate, mb: 1.25, fontSize: '0.9rem' }}>
            Select sports to show in the comparison matrix. Filter by layer or category — all matching
            capabilities update in the table below ({filteredSkills.length} of {MOTOR_CAPABILITIES.length}{' '}
            shown).
          </Typography>

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1.5}
            alignItems={{ lg: 'center' }}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <ToggleButtonGroup
              value={selectedSports}
              onChange={toggleSport}
              aria-label="Sports"
              sx={{
                flexWrap: 'wrap',
                gap: 0.75,
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '8px !important',
                  border: '1px solid #cbd5e1 !important',
                  px: 1.5,
                  color: brandColors.navy,
                },
                '& .Mui-selected': {
                  bgcolor: `${brandColors.navy} !important`,
                  color: '#fff !important',
                },
              }}
            >
              {MOTOR_SPORT_OPTIONS.map((sport) => (
                <ToggleButton key={sport.id} value={sport.id}>
                  {sport.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="motor-layer-filter-label">Framework layer</InputLabel>
              <Select
                labelId="motor-layer-filter-label"
                label="Framework layer"
                value={layerFilter}
                onChange={(e) => setLayerFilter(String(e.target.value))}
              >
                <MenuItem value="all">All layers</MenuItem>
                {layers.map((layer) => (
                  <MenuItem key={layer} value={layer}>
                    {layer}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="motor-category-filter-label">Category</InputLabel>
              <Select
                labelId="motor-category-filter-label"
                label="Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(String(e.target.value))}
              >
                <MenuItem value="all">All categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(layerFilter !== 'all' || categoryFilter !== 'all') && (
              <Chip
                label="Clear filters"
                onDelete={() => {
                  setLayerFilter('all');
                  setCategoryFilter('all');
                }}
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
            {MOTOR_FIT_LEGEND.map((item) => {
              const c = fitColor(item.code);
              return (
                <Chip
                  key={item.code}
                  size="small"
                  label={item.code}
                  title={item.meaning}
                  sx={{ fontWeight: 700, bgcolor: c.bg, color: c.color }}
                />
              );
            })}
          </Stack>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              width: '100%',
              maxHeight: { xs: '70vh', md: 'calc(100vh - 280px)' },
              overflow: 'auto',
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 720 + activeSports.length * 280 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 800,
                      bgcolor: '#f1f5f9',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      minWidth: 180,
                    }}
                  >
                    Capability
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9', minWidth: 110 }}>
                    Category
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9', minWidth: 160 }}>
                    Framework layer
                  </TableCell>
                  {activeSports.map((sport) => (
                    <TableCell
                      key={sport.id}
                      sx={{ fontWeight: 800, bgcolor: '#f1f5f9', minWidth: 260 }}
                      colSpan={1}
                    >
                      {sport.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSkills.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3 + activeSports.length} sx={{ color: brandColors.slate, py: 3 }}>
                      No capabilities match the current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSkills.map((skill) => (
                    <TableRow key={skill.id} hover>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: brandColors.navy,
                          position: 'sticky',
                          left: 0,
                          bgcolor: brandColors.white,
                          zIndex: 1,
                          verticalAlign: 'top',
                        }}
                      >
                        {skill.name}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>{skill.category}</TableCell>
                      <TableCell sx={{ color: brandColors.slate, verticalAlign: 'top', fontSize: '0.8rem' }}>
                        {skill.frameworkLayer}
                      </TableCell>
                      {activeSports.map((sport) => {
                        const mapping = skill.bySport[sport.id] || { application: '—', fit: 'N/A' };
                        const c = fitColor(mapping.fit);
                        return (
                          <TableCell key={`${skill.id}-${sport.id}`} sx={{ verticalAlign: 'top' }}>
                            <Chip
                              size="small"
                              label={mapping.fit || 'N/A'}
                              sx={{ fontWeight: 700, bgcolor: c.bg, color: c.color, mb: 0.75 }}
                            />
                            <Typography
                              sx={{
                                color: brandColors.slate,
                                fontSize: '0.8rem',
                                lineHeight: 1.4,
                              }}
                            >
                              {mapping.application || '—'}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </PublicContentPageShell>
  );
};

export default MotorPsychomotorPage;
